import shortLinkTransfer, {
	getOneLinkParams,
	saveShareParams,
	ShortLinkTransferResult,
	symbolToBearOrBull,
} from "@/utils/shortLinkTransfer";
import { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { isWeixin } from "@/utils/parserDevice";

import HeadMetaContainer from "@/containers/common/HeadMetaContainer";
import { useTranslation } from "next-i18next";
import { getStockInfoFromSymbols } from "@/hooks/useRequest/useInvestingRequest";
import { BetterBuyOptionProd } from "@/interfaces/enums";
import { ReactElement, useEffect } from "react";
import GetAppContainer from "@/containers/common/GetAppContainer";
import handleDeepLink from "@/utils/handleDeepLink";

import Loading from "@/components/GlobalLoading/Loading";
import { useState } from "react";
import Layout from "@/layout/Layout";

interface Props {
	deepLink: string;
	title: string;
	description: string;
	imageUrl: string;
	symbolName?: string;
	nickname?: string;
	symbolType?: string;
	redirectUrl?: string;
	campaign?: string;
	mediaSource?: string;
	channel?: string;
}

export default function IntentShare({
	deepLink,
	title,
	description,
	imageUrl,
	symbolName,
	nickname,
	symbolType,
	redirectUrl,
	campaign,
	mediaSource,
	channel,
}: Props) {
	const { t } = useTranslation("share");
	const [showLoading, setShowLoading] = useState(true);

	useEffect(() => {
		// 存储来源
		saveShareParams(mediaSource, campaign, channel);

		if (redirectUrl) {
			window.location.replace(redirectUrl);
			return;
		}

		if (deepLink && !isWeixin()) {
			handleDeepLink(decodeURI(deepLink));
		}

		setShowLoading(false);
	}, []);

	return (
		<HeadMetaContainer
			title={t(title, { symbolName, type: t(symbolType || ""), symbol: symbolName, nickname })}
			description={t(description)}
			imageUrl={imageUrl}
		>
			{showLoading ? (
				<Loading />
			) : (
				<GetAppContainer deepLink={deepLink ? decodeURI(deepLink) : ""} campaign={campaign} mediaSource={mediaSource} />
			)}
		</HeadMetaContainer>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	let data: ShortLinkTransferResult = {
		deepLink: "",
		title: "",
		description: "",
		imageUrl: "",
	};
	let symbolName = "";
	let symbolType = "";

	try {
		const result = shortLinkTransfer(context.query, context.locale || "en");
		data = {
			...result,
			deepLink: decodeURI(result.deepLink),
		};

		if (result.symbol !== undefined) {
			// 小牛小熊名称
			const type = symbolToBearOrBull(result.symbol);
			if (type === BetterBuyOptionProd.bull) {
				symbolType = "baby-bull";
			} else if (type === BetterBuyOptionProd.bear) {
				symbolType = "baby-bear";
			}

			// 股票名称
			const _stockInfo = await getStockInfoFromSymbols([result.symbol], context.locale || "en");

			if (_stockInfo && _stockInfo[0]) {
				symbolName = _stockInfo[0].name || "";
			}
		}
	} catch (error: any) {}

	const oneLinkParams = getOneLinkParams(context.query);

	if (data?.redirectUrl) {
		const _query = new URLSearchParams();
		if (oneLinkParams.campaign) {
			_query.set("campaign", oneLinkParams.campaign);
			_query.set("inviteCode", oneLinkParams.campaign);
			_query.set("c", oneLinkParams.campaign);
		}
		if (oneLinkParams.mediaSource) {
			_query.set("mediaSource", oneLinkParams.mediaSource);
		}
		if (oneLinkParams.channel) {
			_query.set("channel", oneLinkParams.channel);
		}
		// 透传原始 e/p/i，落地页 / deeplink / OneLink 都不丢
		if (context.query.e) {
			_query.set("e", String(context.query.e));
		}
		if (context.query.p) {
			_query.set("p", String(context.query.p));
		}
		if (context.query.i) {
			_query.set("i", String(context.query.i));
		}
		const separator = data.redirectUrl.includes("?") ? "&" : "?";
		const queryStr = _query.toString();
		const _redirectUrl = queryStr ? data.redirectUrl + separator + queryStr : data.redirectUrl;
		return {
			redirect: {
				destination: _redirectUrl,
				permanent: true,
			},
		};
	}

	return {
		props: {
			...(await serverSideTranslations(context.locale as string, ["share", "common", "daily-fortune"])),
			...data,
			symbolName,
			symbolType,
			locale: context.locale,
			...oneLinkParams,
		},
	};
};

IntentShare.getLayout = function getLayout(page: ReactElement) {
	return <Layout>{page}</Layout>;
};
